import type {
  DateRangeObject,
  ItemAttributes,
  ItemNames,
  JobState,
  WithExclusionObject,
} from '@ps-smart-cp/ppfw-client';

import { Characters, DateRange, ItemName, Locale, ValueSet, WithExclusion } from '@ps-smart-cp/ppfw-client';

interface Schema {
  // ジョブ番号
  readonly jobNo: string;

  // ジョブ名称
  readonly jobFunctionName: string;

  // ジョブ番号範囲
  readonly jobNoRange: WithExclusionObject;

  // ステータス(抽出条件)
  readonly jobStateCondition: Set<JobState>;

  // ステータス
  readonly jobState: JobState;

  // 発行年月日
  readonly dateOfIssueCondition: DateRangeObject;

  // 開始年月日
  readonly startDateCondition: DateRangeObject;

  // 終了年月日
  readonly endDateCondition: DateRangeObject;

  // 発行時間
  readonly dateOfIssue: Date | null;

  // 開始時間
  readonly startDate: Date | null;

  // 終了時間
  readonly endDate: Date | null;

  // 進捗
  readonly progress: string;

  // 読込件数
  readonly readCount: string;

  // 書込件数
  readonly writeCount: string;

  // 処理件数
  readonly processedCount: string;

  // エラー件数
  readonly errorCount: string;
}

const attributes = {
  jobNoRange: WithExclusion.of(Characters.preset.numericalRange({ maxLen: 250 })),
  jobStateCondition: ValueSet.new(),
  dateOfIssueCondition: DateRange.ymd(),
  startDateCondition: DateRange.ymd(),
  endDateCondition: DateRange.ymd(),
} satisfies ItemAttributes<Schema>;

const titleNames = {
  jobMonitoringCompany: ItemName.of([Locale.ja, 'ジョブ監視(会社)']),
};

const names = {
  jobNoRange: ItemName.of([Locale.ja, 'ジョブ番号範囲'], [Locale.en, 'Job No Range']),
  jobStateCondition: ItemName.of([Locale.ja, 'ステータス'], [Locale.en, 'Status']),
  dateOfIssueCondition: ItemName.of([Locale.ja, '発行年月日'], [Locale.en, 'Date Of Issue']),
  startDateCondition: ItemName.of([Locale.ja, '開始年月日'], [Locale.en, 'Start Date']),
  endDateCondition: ItemName.of([Locale.ja, '終了年月日'], [Locale.en, 'End Date']),
} satisfies ItemNames<Schema>;

const labelNames = {
  resultLog: ItemName.of([Locale.ja, '結果ログ'], [Locale.en, 'Result Log']),
  outputFile: ItemName.of([Locale.ja, '出力ファイル'], [Locale.en, 'Output File']),
  jobNo: ItemName.of([Locale.ja, 'ジョブ番号'], [Locale.en, 'Job No']),
  jobFunctionName: ItemName.of([Locale.ja, '機能名'], [Locale.en, 'Job Function']),
  progress: ItemName.of([Locale.ja, '進捗'], [Locale.en, 'Progress']),
  dateOfIssue: ItemName.of([Locale.ja, '発行時間'], [Locale.en, 'Data Of Issue']),
  startDate: ItemName.of([Locale.ja, '開始時間'], [Locale.en, 'Start Date']),
  endDate: ItemName.of([Locale.ja, '終了時間'], [Locale.en, 'End Date']),
  errorLog: ItemName.of([Locale.ja, 'エラーログ'], [Locale.en, 'Error Log']),
  readCount: ItemName.of([Locale.ja, '読込件数']),
  writeCount: ItemName.of([Locale.ja, '書込件数']),
  processedCount: ItemName.of([Locale.ja, '処理件数']),
  errorCount: ItemName.of([Locale.ja, 'エラー件数']),
  items: ItemName.of([Locale.ja, '件'], [Locale.ja, 'Items']),
  jobInfo: ItemName.of([Locale.ja, 'ジョブ情報']),
  processingInfo: ItemName.of([Locale.ja, '処理結果情報']),
  additionalInfo: ItemName.of([Locale.ja, '追加情報']),
  command: ItemName.of([Locale.ja, '操作'], [Locale.en, 'Command']),
};

interface Record {
  readonly jobNo: string;
  readonly jobFunctionName: string;
  readonly jobNoRange: string;
  readonly isJobNoRangeExcluded: boolean;
  readonly jobStateCondition: string[];
  readonly dateOfIssueFrom: string;
  readonly dateOfIssueTo: string;
  readonly startDateFrom: string;
  readonly startDateTo: string;
  readonly endDateFrom: string;
  readonly endDateTo: string;
  readonly completionTime: string;
  readonly jobStateForDisplay: string;
  readonly jobState: string;
  readonly dateOfIssue: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly resultLog: string;
  readonly progress: string;
  readonly progressAndErrorLog?: string;
  readonly readCount?: string;
  readonly writeCount?: string;
  readonly processedCount?: string;
  readonly errorCount?: string;
}

const jobWatch = {
  names,
  labelNames,
  titleNames,
  attributes,
};

interface JobWatch {
  schema: Schema;
  record: Record;
}

export { jobWatch };
export type { JobWatch };
